//import { CameraIcon } from "@components/Icons/CameraIcon"
//import { PlusIcon } from "@components/Icons/PlusIcon"
import { GradientButton } from '@components/Button';
import { PauseIcon } from '@components/Icons/PauseIcon';
import { PlayIcon } from '@components/Icons/PlayIcon';
import { PlayNextIcon } from '@components/Icons/PlayNextIcon';
import { PrevIcon } from '@components/Icons/PrevIcon';
import { useStationMode } from '@hooks/station';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import { useSessionFeatureState } from '@hooks/storeHooks/useSessionState';
import { getContributorById } from '@services/api.ts';
import { sendSelectCompanionMessage } from '@utils/station';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
// import { Likedicon } from "@components/Icons/LikedIcon";
// import { FavoriteIcon } from "@components/Icons/FavoriteIcon";
// import useAlbumData from "@hooks/useAlbumData";

export const MusicInfo = ({
  progress,
  onSeek,
  currentTime = 0,
  duration = 0,
  formatTime,
  playlistOpen = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  volume: _volume = 1, // Kept for commented volume reduction code (may be needed later)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onVolumeChange: _onVolumeChange, // Kept for commented volume reduction code (may be needed later)
  isPlaying,
  onPlayPause,
  onNext,
  onPrev,
  albumArtUrl,
  creatorName,
  contributorId,
  slug,
  videoPosterUrl,
}: {
  progress: number;
  onSeek: (p: number) => void;
  currentTime?: number;
  duration?: number;
  formatTime?: (seconds: number) => string;
  playlistOpen?: boolean;
  volume?: number;
  onVolumeChange?: (volume: number) => void;
  slug: string;
  isPlaying?: boolean;
  onPlayPause?: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  albumArtUrl?: string;
  creatorName?: string;
  contributorId?: string;
  videoPosterUrl?: string;
}) => {
  const { activePlaylist } = useMusicState();
  const { setIsMinimized } = useCommonState();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setCompanionSlug } = useSessionFeatureState();
  const isStationMode = useStationMode();
  const currentTrack = activePlaylist
    ? activePlaylist.album.tracks[activePlaylist.currentIndex]
    : null;

  // For playlists, use track contributors; for albums, use album contributors
  const currentContributor =
    activePlaylist?.album?.type === 'playlist'
      ? currentTrack?.contributors?.[0]
      : activePlaylist?.album?.contributors?.[0];

  const artistImage = currentContributor?.thumbnailUrl;
  const barRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragProgress, setDragProgress] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [isSearchDropdownOpen] = useState(false);
  const seekTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const targetProgressRef = useRef<number | null>(null);

  // Check if current album is an audiobook
  const isAudiobook = activePlaylist?.album?.category === 'book';
  const calcPercentage = useCallback((clientX: number) => {
    if (!barRef.current) return 0;
    const rect = barRef.current.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    return (x / rect.width) * 100;
  }, []);
  const handleContributorClick = useCallback(() => {
    if (currentContributor?.id) {
      // Minimize the player first (like sidebar navigation)
      setIsMinimized(true);
      // Then navigate to contributor profile
      navigate(`/contributor/${currentContributor.id}`);
    }
  }, [currentContributor, navigate, setIsMinimized, isPlaying, onPlayPause]);

  const handleClick = (e: React.MouseEvent) => {
    // Check if seeking is possible
    if (duration <= 0 || isNaN(duration)) {
      console.warn('Seeking disabled: Invalid duration', { duration });
      return;
    }

    const percentage = calcPercentage(e.clientX);
    onSeek(percentage);
  };

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (duration <= 0 || isNaN(duration)) {
      console.warn('Dragging disabled: Invalid duration', { duration });
      return;
    }

    e.preventDefault();
    if (seekTimeoutRef.current) {
      clearTimeout(seekTimeoutRef.current);
      seekTimeoutRef.current = null;
    }
    setIsSeeking(false);
    setIsDragging(true);
    const percentage =
      'clientX' in e
        ? calcPercentage(e.clientX)
        : calcPercentage(e.touches[0].clientX);
    setDragProgress(percentage);
    onSeek(percentage);
  };

  const handleDragMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      const percentage =
        'clientX' in e
          ? calcPercentage(e.clientX)
          : calcPercentage(e.touches[0].clientX);
      setDragProgress(percentage);
      onSeek(percentage);
    },
    [isDragging, onSeek, calcPercentage],
  );

  const handleDragEnd = useCallback(() => {
    if (seekTimeoutRef.current) {
      clearTimeout(seekTimeoutRef.current);
    }
    targetProgressRef.current = dragProgress;
    setIsSeeking(true);
    onSeek(dragProgress);
    setIsDragging(false);
    seekTimeoutRef.current = setTimeout(() => {
      setIsSeeking(false);
      targetProgressRef.current = null;
    }, 300);
  }, [dragProgress, onSeek]);

  const handleConnect = async () => {
    // Pause music when connecting to session
    if (isPlaying && onPlayPause) {
      console.log(
        '🎵 Pausing music player on connect button click (MusicInfo)',
      );
      onPlayPause();
    }
    // OLD CODE: Volume reduction
    // if (_volume > 0.2 && _onVolumeChange) {
    //   _onVolumeChange(0.2);
    // }

    if (isStationMode) {
      if (!contributorId) {
        toast.error(t('player.companionFailed'));
        return;
      }

      try {
        const data = await getContributorById(contributorId);
        if (data?.companionId) {
          sendSelectCompanionMessage(data.companionId);
        } else {
          toast.error(
            t('player.companionFailed'),
          );
        }
      } catch (error) {
        console.error('Error fetching contributor:', error);
        toast.error(t('player.companionFailed'));
      }
      return;
    }

    if (slug && slug.trim() !== '' && !slug.includes(' ')) {
      setCompanionSlug({
        companionSlug: slug,
        isSessionPage: false,
        autoConnect: true,
      });
      return;
    }

    if (!contributorId) {
      toast.error(t('player.companionFailed'));
      return;
    }

    try {
      const data = await getContributorById(contributorId);
      if (data?.companionSlug) {
        setCompanionSlug({
          companionSlug: data.companionSlug,
          isSessionPage: false,
          autoConnect: true,
        });
      } else {
        toast.error(t('player.companionFailed'));
      }
    } catch (error) {
      console.error('Error fetching contributor:', error);
      toast.error(t('player.companionFailed'));
    }
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove as any);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove as any);
      window.addEventListener('touchend', handleDragEnd);
    } else {
      window.removeEventListener('mousemove', handleDragMove as any);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove as any);
      window.removeEventListener('touchend', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove as any);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove as any);
      window.removeEventListener('touchend', handleDragEnd);
      if (seekTimeoutRef.current) {
        clearTimeout(seekTimeoutRef.current);
      }
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  useEffect(() => {
    if (
      isSeeking &&
      targetProgressRef.current !== null &&
      Math.abs(progress - targetProgressRef.current) < 1
    ) {
      if (seekTimeoutRef.current) {
        clearTimeout(seekTimeoutRef.current);
      }
      setIsSeeking(false);
      targetProgressRef.current = null;
    }
  }, [progress, isSeeking]);

  useEffect(() => {
    // Clear any pending seek timeout
    if (seekTimeoutRef.current) {
      clearTimeout(seekTimeoutRef.current);
      seekTimeoutRef.current = null;
    }
    setDragProgress(0);
    setIsSeeking(false);
    setIsDragging(false);
    targetProgressRef.current = null;
  }, [activePlaylist?.currentIndex]);

  // const activeTrack = activePlaylist ? activePlaylist.album.tracks[activePlaylist.currentIndex] : null;
  return (
    <div
      className={`absolute bottom-0 left-0 ${
        isSearchDropdownOpen ? 'z-[9999]' : 'z-[11]'
      } bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.9)_59.06%)] pt-16 pb-8 px-0 transition-all duration-300 ${
        playlistOpen && !isStationMode
          ? 'sm:w-[calc(100%-384px)] w-full'
          : 'w-full'
      }`}>
      <div
        className={`flex px-4 flex-col transition-all duration-300 w-full max-w-[400px] ${
          isStationMode ? '' : 'md:pb-[80px]'
        }`}>
        {/* Album Art with Vinyl Record - Use video poster for my-creations, hide for audiobooks */}
        {albumArtUrl && !isAudiobook && (
          <div
            className={`mb-2 relative w-[73px] h-[73px] ${
              isStationMode ? '' : 'md:mb-3 md:w-[132px] md:h-[132px]'
            }`}>
            {/* Vinyl Record - behind */}
            <div
              className={`absolute top-0 left-[18px] w-[73px] h-[73px] rounded-full overflow-hidden shadow-lg z-[5] ${
                isStationMode ? '' : 'md:left-[32px] md:w-[132px] md:h-[132px]'
              }`}>
              <img
                src="/assets/images/album.png"
                alt="vinyl record"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Album Cover or Video Poster - on top */}
            <div
              className={`absolute left-0 top-0 w-[73px] h-[73px] overflow-hidden bg-gray-800 shadow-lg rounded-[8px] z-[10] ${
                isStationMode ? '' : 'md:w-[132px] md:h-[132px]'
              }`}>
              <img
                src={videoPosterUrl || albumArtUrl}
                alt={videoPosterUrl ? 'video poster' : 'album art'}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

        {/* Created by text - only for creations */}
        {creatorName && !creatorName.includes('undefined') && (
          <div
            className={`text-[#FFFFFF] font-[DM_Sans] font-[400] text-[14px] text-left ${
              isStationMode ? '' : 'md:text-[16px]'
            }`}>
            {t('player.createdBy', { name: creatorName })}
          </div>
        )}

        <div
          className={`flex text-[36px] italic font-normal leading-[120%] text-[#FCFCFC] text-left font-[DM_Serif_Text] ${
            isStationMode ? '' : 'sm:text-[48px]'
          }`}>
          {currentTrack ? currentTrack.name : ''}
        </div>
        <div
          className={`flex pt-2 items-center gap-[8px] ${
            isStationMode ? '' : 'md:hidden'
          }`}>
          <div className="flex" onClick={handleContributorClick}>
            {artistImage && (
              <img
                className="w-[24px] h-[24px] object-cover object-center rounded-full"
                src={artistImage}
                alt=""
              />
            )}
          </div>
          <div
            className="flex text-[13px] font-normal leading-[130%] text-left text-white/80"
            onClick={handleContributorClick}>
            {currentContributor?.givenName}
          </div>

          {/* Only show Connect button if slug is valid OR contributorId is available */}
          {/* === DEMO: Hide Connect for edo-segal - REMOVE AFTER DEMO === */}
          {((slug && slug.trim() !== '' && !slug.includes(' ')) ||
            (contributorId && contributorId.trim() !== '')) && (
            <div className="flex pl-2">
              <div onClick={handleConnect}>
                <GradientButton icon="" title={t('player.connect')} />
              </div>
            </div>
          )}

          {/* <div className="flex items-center cursor-pointer gap-[3px] z-[9999]" onClick={() => activeTrack?.id && addFavouriteItem('contributor', activePlaylist?.album.contributors[0]?.id, activePlaylist?.album.contributors[0]?.isLiked)}>
                        {activeTrack?.isLiked ? (
                            <Likedicon width={10} height={10} />
                        ) : (
                            <FavoriteIcon width={10} height={11} />
                        )}

                    </div> */}
          {/* <button
                        className="text-[10px] font-normal leading-[140%] text-white/60 text-left p-[4px] border-1 border-white/20 rounded-[4px]">
                        Follow
                    </button> */}
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full text-white pb-6 px-4">
        {/* Time Display */}
        {formatTime && (
          <div className="flex justify-between text-white/60 text-xs mt-4 mb-2">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        )}
        <div
          ref={barRef}
          className={`flex w-full h-[4px] bg-white/20 relative rounded-full ${
            duration > 0 && !isNaN(duration)
              ? 'cursor-pointer'
              : 'cursor-not-allowed opacity-50'
          }`}
          onClick={handleClick}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          title={
            duration > 0 && !isNaN(duration)
              ? t('player.seekTooltip')
              : t('player.seekUnavailable')
          }>
          <div
            className={`absolute left-0 top-0 h-full bg-white/60 rounded-full ${
              isDragging || isSeeking
                ? ''
                : 'transition-all duration-100 ease-linear'
            }`}
            style={{
              width: `${Math.max(
                isDragging || isSeeking ? dragProgress : progress,
                0,
              )}%`,
            }}
          />
          <div
            className={`absolute top-1/2 -translate-y-1/2 w-[12px] h-[12px] rounded-full bg-white shadow ${
              isDragging || isSeeking
                ? ''
                : 'transition-all duration-100 ease-linear'
            }`}
            style={{
              left: `calc(${Math.max(
                isDragging || isSeeking ? dragProgress : progress,
                0,
              )}% - 6px)`,
            }}
          />
        </div>
      </div>

      {/* Mobile Bottom Controls - Prev/Play/Next */}
      <div
        className={`w-full flex justify-center ${
          isStationMode ? '' : 'sm:hidden'
        }`}>
        <div className="flex items-center gap-6">
          {/* Previous Button */}
          <button
            onClick={onPrev}
            className="flex w-[48px] h-[48px] rounded-full items-center justify-center bg-black/50 backdrop-blur-sm hover:bg-white/10 transition-colors">
            <PrevIcon width={14} height={16} />
          </button>

          {/* PLAY / PAUSE - Main Button */}
          <button
            onClick={onPlayPause}
            className="flex w-[56px] h-[56px] rounded-full items-center justify-center bg-[#1A1919] text-black hover:bg-white/15 transition-colors">
            {isPlaying ? <PauseIcon /> : <PlayIcon width={24} height={24} />}
          </button>

          {/* Next Button */}
          <button
            onClick={onNext}
            className="flex w-[48px] h-[48px] rounded-full items-center justify-center bg-black/50 backdrop-blur-sm hover:bg-white/10 transition-colors">
            <PlayNextIcon width={14} height={16} />
          </button>
        </div>
      </div>

      {/*<div className="flex gap-[8px] px-4">
        <div className="flex">
                    <button className="flex w-[44px] h-[44px] items-center justify-center bg-[#fcfcfc]/10 rounded-full">
                        <PlusIcon/>
                    </button>
                </div>
        <SearchBar
          placeholder={t('header.askOrSearch')}
          containerClassName="w-full"
          className="bg-white/10"
          dropdownClassName="z-[99999]"
          onDropdownStateChange={setIsSearchDropdownOpen}
        />
         <div className="flex">
          <button>
            <img
              className="flex w-[44px] h-[44px] min-w-[44px] max-w-full object-cover rounded-full"
              src="/assets/images/chat_avatar.png"
              alt="Companion"
            />
          </button>
        </div>
      </div>*/}
    </div>
  );
};
